/**
 * Cabeçalho do contato, acima do chat.
 *
 * Mostra foto, nome e número de quem está do outro lado MESMO SEM CADASTRO —
 * era o que faltava: a tela dizia só "Contato sem cadastro" e quem atendia não
 * tinha como saber com quem estava falando nem como ligar de volta.
 *
 * Duas honestidades que a tela precisa manter:
 * - contato anônimo (@lid) do WhatsApp não tem número para mostrar. Em vez de
 *   exibir os dígitos do identificador, que não são telefone, ela diz que o
 *   número não foi revelado e o que fazer a respeito;
 * - o nome vem do perfil do WhatsApp — é o que a pessoa escolheu exibir, não um
 *   nome verificado. Vai como sugestão no cadastro, com o rótulo à vista.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResource } from '../lib/useResource.js';
import { get, post } from '../lib/api.js';
import { Alert, ErrorBox, Field, Modal, useSubmit } from './ui.js';
import { IconeVoltar, IconeTresPontos } from './Icones.js';

interface Contato {
  conversationId: string;
  telefone: string | null;
  nomeNoWhatsapp: string | null;
  foto: string | null;
  identificadorAnonimo: boolean;
  customerId: string | null;
  nomeCadastrado: string | null;
  sincronizadoEm: string | null;
  porqueSemNumero: string | null;
}

interface VeiculoNaFicha {
  id: string;
  marca?: string | null;
  modelo?: string | null;
  ano?: number | null;
  motorizacao?: string | null;
  cambio?: string | null;
}

interface Sugestao {
  jaCadastrado: boolean;
  customerId: string | null;
  telefone: string | null;
  nomeSugerido: string | null;
  identificadorAnonimo: boolean;
  pedido: string | null;
  veiculos: VeiculoNaFicha[];
  veiculoEmFocoId: string | null;
}

function descreverVeiculo(v: VeiculoNaFicha): string {
  const partes = [v.marca, v.modelo, v.ano ? String(v.ano) : null, v.motorizacao, v.cambio];
  return partes.filter(Boolean).join(' ') || 'veículo sem identificação';
}

export function ContatoDaConversa({
  conversationId,
  podeCadastrar,
  onCadastrou,
  onVoltar,
  acoes,
  acaoPrincipal,
}: {
  conversationId: string;
  podeCadastrar: boolean;
  onCadastrou: () => void;
  onVoltar?: () => void;
  /** Ações da conversa. Vão para o menu ⋮, não para a barra. */
  acoes?: ReactNode;
  /** A única ação que fica visível na barra. */
  acaoPrincipal?: ReactNode;
}) {
  const navigate = useNavigate();
  const [recarga, setRecarga] = useState(0);
  const contato = useResource<{ contato: Contato }>(
    `/api/conversations/${conversationId}/contato`,
    [conversationId, recarga],
  );
  const [formAberto, setFormAberto] = useState(false);

  const c = contato.data?.contato;

  const atualizar = useSubmit(async () => {
    await get(`/api/conversations/${conversationId}/contato?atualizar=1`);
    setRecarga((r) => r + 1);
  });

  const nome = c?.nomeCadastrado ?? c?.nomeNoWhatsapp ?? 'Contato sem cadastro';
  const cadastrado = Boolean(c?.customerId);

  const [menu, setMenu] = useState(false);

  return (
    <>
      {/* Uma linha só: voltar, foto, nome e telefone, menu. Os botões grandes
          de antes empurravam o telefone para fora e ele quebrava letra por
          letra — foi o que apareceu na tela do proprietário. */}
      <header className="wa-topo">
        {onVoltar ? (
          <button type="button" className="wa-icone" aria-label="Voltar" onClick={onVoltar}>
            <IconeVoltar size={22} />
          </button>
        ) : null}

        <Avatar foto={c?.foto ?? null} nome={nome} />

        <div className="wa-topo-texto">
          <div className="wa-topo-nome">{nome}</div>
          <div className="wa-topo-sub">
            {c?.telefone ? (
              formatarTelefone(c.telefone)
            ) : (
              <span>{c?.identificadorAnonimo ? 'número não revelado' : '—'}</span>
            )}
            {/* "sem cadastro" sai daqui: o botão Cadastrar ao lado já diz isso,
                e o texto roubava o espaço do número, que é o que se precisa ler. */}
          </div>
        </div>

        {/* Só a ação do momento fica na barra. Cadastrar é importante, mas é
            de uma vez só; assumir a conversa é o que se faz no meio do
            atendimento, com o cliente esperando do outro lado. */}
        {acaoPrincipal}

        <button
          type="button"
          className="wa-icone"
          aria-label="Mais ações"
          aria-expanded={menu}
          onClick={() => setMenu((v) => !v)}
        >
          <IconeTresPontos size={22} />
        </button>

        {menu ? (
          <>
            <div className="wa-fundo" onClick={() => setMenu(false)} />
            <div className="wa-menu" role="menu" onClick={() => setMenu(false)}>
              {!cadastrado && podeCadastrar ? (
                <button type="button" onClick={() => setFormAberto(true)}>
                  Cadastrar cliente
                </button>
              ) : null}
              {acoes}
              <button type="button" disabled={atualizar.saving} onClick={() => void atualizar.submit()}>
                {atualizar.saving ? 'Buscando…' : 'Atualizar contato'}
              </button>
              {cadastrado ? (
                <button type="button" onClick={() => navigate(`/clientes/${c?.customerId}`)}>
                  Ver ficha do cliente
                </button>
              ) : null}
            </div>
          </>
        ) : null}
      </header>

      <ErrorBox error={contato.error ?? atualizar.error} />

      {formAberto ? (
        <FormularioDeCadastro
          conversationId={conversationId}
          onFechar={() => setFormAberto(false)}
          onPronto={() => {
            setFormAberto(false);
            setRecarga((r) => r + 1);
            onCadastrou();
          }}
        />
      ) : null}
    </>
  );
}

function Avatar({ foto, nome }: { foto: string | null; nome: string }) {
  const [quebrou, setQuebrou] = useState(false);
  const iniciais = nome
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

  if (foto && !quebrou) {
    return (
      <img
        src={foto}
        alt={`Foto de perfil de ${nome}`}
        onError={() => setQuebrou(true)}
        style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div
      aria-hidden
      style={{
        width: 52,
        height: 52,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--gl-accent)',
        color: 'var(--gl-accentStrong)',
        fontWeight: 700,
      }}
    >
      {iniciais || '?'}
    </div>
  );
}

/** +5571984243307 -> +55 (71) 98424-3307 */
function formatarTelefone(e164: string): string {
  const m = /^\+55(\d{2})(\d{4,5})(\d{4})$/.exec(e164);
  return m ? `+55 (${m[1]}) ${m[2]}-${m[3]}` : e164;
}

/**
 * Formulário já preenchido com o que o assistente levantou. Redigitar num
 * formulário em branco é onde a qualificação se perde — ninguém redigita no
 * meio de um atendimento corrido.
 */
function FormularioDeCadastro({
  conversationId,
  onFechar,
  onPronto,
}: {
  conversationId: string;
  onFechar: () => void;
  onPronto: () => void;
}) {
  const sugestao = useResource<{ sugestao: Sugestao }>(
    `/api/conversations/${conversationId}/cadastro`,
    [conversationId],
  );
  const s = sugestao.data?.sugestao;

  const [nome, setNome] = useState<string | null>(null);
  const [telefone, setTelefone] = useState<string | null>(null);
  const [desmarcados, setDesmarcados] = useState<string[]>([]);

  const valorNome = nome ?? s?.nomeSugerido ?? '';
  const valorTelefone = telefone ?? s?.telefone ?? '';
  const veiculos = s?.veiculos ?? [];
  const escolhidos = veiculos.map((v) => v.id).filter((id) => !desmarcados.includes(id));

  const salvar = useSubmit(async () => {
    await post(`/api/conversations/${conversationId}/cadastro`, {
      nome: valorNome.trim(),
      telefone: valorTelefone.trim() || null,
      veiculoIds: escolhidos,
      observacoes: s?.pedido ? `Pedido na conversa: ${s.pedido}` : null,
    });
    onPronto();
  });

  return (
    <Modal title="Cadastrar cliente" onClose={onFechar}>
      <ErrorBox error={sugestao.error ?? salvar.error} />

      {s?.jaCadastrado ? (
        <Alert tone="info" title="Esta conversa já está ligada a um cliente." />
      ) : null}

      <Field
        label="Nome"
        required
        hint={s?.nomeSugerido ? 'Veio do perfil do WhatsApp. Corrija se não for o nome real.' : undefined}
      >
        <input value={valorNome} onChange={(e) => setNome(e.target.value)} />
      </Field>

      <Field
        label="Telefone"
        required
        hint={
          s?.telefone
            ? 'Confirmado pelo WhatsApp.'
            : 'O WhatsApp não revelou o número deste contato — peça à pessoa na conversa.'
        }
      >
        <input
          value={valorTelefone}
          onChange={(e) => setTelefone(e.target.value)}
          placeholder="+55 71 98424-3307"
        />
      </Field>

      {s?.pedido ? (
        <Field label="O que a pessoa quer" hint="Vai para as observações do cadastro.">
          <input value={s.pedido} readOnly />
        </Field>
      ) : null}

      {veiculos.length > 0 ? (
        <fieldset style={{ border: '1px solid var(--gl-accentBorder)', borderRadius: 8, padding: 10 }}>
          <legend className="hint">Veículos levantados na conversa</legend>
          {veiculos.map((v) => (
            <label key={v.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
              <input
                type="checkbox"
                checked={escolhidos.includes(v.id)}
                onChange={(e) =>
                  setDesmarcados((d) =>
                    e.target.checked ? d.filter((x) => x !== v.id) : [...d, v.id],
                  )
                }
              />
              <span>
                {descreverVeiculo(v)}
                {v.id === s?.veiculoEmFocoId ? <span className="hint"> — em questão</span> : null}
                {!v.modelo ? <span className="hint"> (sem modelo: não será criado)</span> : null}
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <button
        type="button"
        className="button button-primary"
        style={{ marginTop: 12 }}
        disabled={salvar.saving || valorNome.trim().length < 2 || valorTelefone.trim().length < 8}
        onClick={() => void salvar.submit()}
      >
        {salvar.saving ? 'Cadastrando…' : 'Cadastrar'}
      </button>
    </Modal>
  );
}
